import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { t } from "@lingui/macro";
import useTieredMembership from "@/components/Membership/useTieredMembership";
import usePlanPurchase, { didPurchaseSucceed } from "@/components/Membership/usePlanPurchase";
import { useLogEvent } from "@/services/Analytics/useLogEvent";
import {
  ClickTarget,
  EventType,
  PLAN_CLICK_TARGET,
  SourcePage,
} from "@/services/Analytics/EventTypes";
import { Plan } from "@WahooFitness/redesignr";

// ─── Hook ─────────────────────────────────────────────────────────────────────

// `isFeaturePaywall` marks the genuine feature paywall (the /paywall route, deep-linked
// from a locked feature), where tiers that aren't relevant to the upsell are trimmed. It
// defaults to false so every other surface shows the full set of tiers — notably the
// membership entry point, which falls back to this paywall when the user has no active
// sub (e.g. an expired SYSTM subscription) and must always offer every option.
function usePaywall(onPurchaseSuccess?: () => void, isFeaturePaywall = false) {
  const [searchParams] = useSearchParams();
  const {
    proPkg,
    corePkg,
    offeringsLoading,
    activeTier,
    customerInfo,
    userSubscription,
    convertPeriodToDays,
  } = useTieredMembership();
  const { purchasePlan, purchaseInProgress } = usePlanPurchase();
  const { logEvent } = useLogEvent();

  const currentTier: Plan = activeTier;

  // ─── URL params ───────────────────────────────────────────────────────────

  const featureName = searchParams.get("feature");
  const tierParam = searchParams.get("tier");
  const recommendedTier: Plan = tierParam === "core" ? "core" : "pro";

  // ─── Trial eligibility ────────────────────────────────────────────────────

  // RevenueCat flags trial eligibility per package, and trials exist on both Core
  // and Pro (one trial total per user — using it on either tier consumes it).
  // Pre-selection surfaces (header, card visibility) use "any trial available";
  // the CTA and disclaimer key off the selected package's own flag.
  const proTrialAvailable = !!proPkg?.product.trialAvailable;
  const trialAvailable = proTrialAvailable || !!corePkg?.product.trialAvailable;

  // The Core card's trial badge reflects Core's own eligibility — never when Core is
  // already the current plan.
  const coreTrialAvailable = currentTier !== "core" && !!corePkg?.product.trialAvailable;

  // ─── Card visibility ──────────────────────────────────────────────────────

  const isCoreSelectable = currentTier !== "core";

  // The membership surface always shows every tier. Only the feature paywall trims the
  // ones that aren't relevant to the upsell, each via a single narrow hide case.
  //
  // Base hides for a current Core subscriber on a Pro feature — a strict downgrade they
  // won't pick.
  const showBase = !isFeaturePaywall || !(recommendedTier === "pro" && currentTier === "core");
  // Core hides for a Pro feature with no trial — the user just sees Pro vs their current
  // plan. A Core feature, a current Core subscriber, or an available trial keep Core
  // relevant.
  const hideCoreOnProPaywall =
    recommendedTier === "pro" && currentTier !== "core" && !trialAvailable;
  const showCore = !isFeaturePaywall || !hideCoreOnProPaywall;

  // ─── Selected plan ────────────────────────────────────────────────────────

  const [selectedPlan, setSelectedPlan] = useState<Plan>(recommendedTier);

  // Re-select the recommended plan when the URL-driven recommended tier changes.
  useEffect(() => {
    setSelectedPlan(recommendedTier);
  }, [recommendedTier]);

  // A core user looking at a Pro feature gets no "Recommended" badge (trial or not):
  // Pro isn't a clear upsell when they already hold Core.
  const showRecommendedBadge = !(recommendedTier === "pro" && currentTier === "core");

  const selectedPkg = selectedPlan === "core" ? corePkg : selectedPlan === "pro" ? proPkg : null;
  const selectedPriceString = selectedPkg?.product.priceString;

  const selectedTrialAvailable = !!selectedPkg?.product.trialAvailable;
  const selectedTrialDays = convertPeriodToDays(selectedPkg?.product.trialLength);

  // Header copy needs a trial length before any package-specific choice applies:
  // prefer the selected package's trial, fall back to whichever package has one.
  const trialPkg = selectedTrialAvailable ? selectedPkg : proTrialAvailable ? proPkg : corePkg;
  const trialDays = convertPeriodToDays(trialPkg?.product.trialLength);

  // ─── Analytics helpers ────────────────────────────────────────────────────

  const sourcePage = recommendedTier === "core" ? SourcePage.core_paywall : SourcePage.pro_paywall;

  const ctaClickTarget = selectedTrialAvailable
    ? ClickTarget.paywall_start_trial
    : ClickTarget.paywall_upgrade;

  // ─── Display text ─────────────────────────────────────────────────────────

  const headerTitle = useMemo(() => {
    if (featureName) return featureName;
    if (trialAvailable) return t`Start your ${trialDays}-day free trial`;
    return t`Unlock your full potential`;
  }, [featureName, trialAvailable, trialDays]);

  const subtitleText = useMemo(
    () =>
      recommendedTier === "core"
        ? t`Upgrade to Core or Pro to access this feature`
        : t`Upgrade to Pro to access this feature`,
    [recommendedTier]
  );

  const buttonText = useMemo(() => {
    if (selectedTrialAvailable) return t`Start your ${selectedTrialDays}-day free trial`;
    if (selectedPlan === "core") return t`Upgrade to Core`;
    return t`Upgrade to Pro`;
  }, [selectedPlan, selectedTrialAvailable, selectedTrialDays]);

  const disclaimerText = useMemo(() => {
    if (!selectedPriceString) return "";
    if (selectedTrialAvailable) {
      return t`${selectedTrialDays} day free trial, then ${selectedPriceString}/month. Cancel anytime by managing your account.`;
    }
    return t`${selectedPriceString}/month. Cancel anytime by managing your account.`;
  }, [selectedPriceString, selectedTrialAvailable, selectedTrialDays]);

  // ─── Actions ──────────────────────────────────────────────────────────────

  const onPlanSelect = useCallback(
    (plan: Plan) => {
      setSelectedPlan(plan);
      logEvent("paywall_plan_selected", {
        event_type: EventType.click,
        click_target: PLAN_CLICK_TARGET[plan],
        source_page_name: sourcePage,
      });
    },
    [logEvent, sourcePage]
  );

  const logDismiss = useCallback(
    (target: ClickTarget) => {
      logEvent("paywall_dismissed", {
        event_type: EventType.dismiss,
        click_target: target,
        source_page_name: sourcePage,
      });
    },
    [logEvent, sourcePage]
  );

  const onCtaPress = useCallback(async () => {
    logEvent("paywall_cta", {
      event_type: EventType.click,
      click_target: ctaClickTarget,
      source_page_name: sourcePage,
    });
    // Close the paywall on success so the user isn't left on it with the CTA still showing;
    // a cancelled/failed purchase leaves it open for a retry (see didPurchaseSucceed).
    const result = await purchasePlan(selectedPlan);
    if (didPurchaseSucceed(result)) {
      onPurchaseSuccess?.();
    }
  }, [logEvent, ctaClickTarget, sourcePage, selectedPlan, purchasePlan, onPurchaseSuccess]);

  return {
    // Card data
    selectedPlan,
    proPkg,
    corePkg,
    // Card visibility
    showBase,
    showCore,
    isCoreSelectable,
    coreTrialAvailable,
    showRecommendedBadge,
    // Tier / plan state
    recommendedTier,
    currentTier,
    // Display text
    headerTitle,
    subtitleText,
    buttonText,
    disclaimerText,
    // Actions
    onPlanSelect,
    onCtaPress,
    logDismiss,
    // Purchase state
    purchaseInProgress,
    offeringsLoading,
    // RevenueCat
    customerInfo,
    userSubscription,
  };
}

export default usePaywall;
